import { useEffect } from "react";

const BLOCKED_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "NOSCRIPT",
  "TEXTAREA",
  "INPUT",
  "SELECT",
  "OPTION",
  "PRE",
  "CODE",
  "SVG",
]);

const EMAIL_REGEX = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const WORD_REGEX = /[A-Za-z]+(?:'[A-Za-z]+)?/g;
const ACRONYMS = new Set([
  "am",
  "pm",
  "sms",
  "api",
  "crm",
  "url",
  "id",
  "otp",
  "ui",
  "ux",
  "faq",
  "dob",
  "gst",
  "pdf",
  "csv",
  "json",
  "xml",
  "sql",
]);
const CONNECTORS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "from",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "vs",
  "with",
]);
const DIVIDER_CHARS = new Set([":", "-", "/", "(", "["]);

const looksLikeUserCodeText = (value) => {
  const text = String(value || "").trim();
  if (!text) return false;
  if (/[_-]/.test(text) && /[A-Za-z]/.test(text)) return true;
  if (/^[A-Za-z0-9_:-]+$/.test(text) && /\d/.test(text)) return true;
  return false;
};

const hasSkippableAncestor = (node) => {
  let current = node?.parentElement;
  while (current) {
    if (current.isContentEditable) return true;
    if (current.dataset?.noTextNormalize === "true") return true;
    if (BLOCKED_TAGS.has(current.tagName)) return true;
    current = current.parentElement;
  }
  return false;
};

const normalizePlainTextCase = (text) => {
  let wordIndex = 0;
  return String(text || "").replace(WORD_REGEX, (word, offset) => {
    const lower = word.toLowerCase();
    const isFirstWord = wordIndex === 0;
    const prevChar = offset > 0 ? text[offset - 1] : "";
    wordIndex += 1;

    if (ACRONYMS.has(lower)) return lower.toUpperCase();
    if (!isFirstWord && !DIVIDER_CHARS.has(prevChar) && CONNECTORS.has(lower)) {
      return lower;
    }
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  });
};

const normalizeDisplayText = (value) => {
  if (!value) return value;
  const raw = String(value);
  if (!raw.trim()) return raw;
  if (looksLikeUserCodeText(raw)) return raw;

  // Keep templating tokens untouched so runtime interpolation/parity is never affected.
  const templateTokens = [];
  let protectedText = raw.replace(/{{[^}]+}}|{[^}\s][^}]*}/g, (match) => {
    const token = `\u0001${templateTokens.length}\u0002`;
    templateTokens.push(match);
    return token;
  });

  const emails = [];
  protectedText = protectedText.replace(EMAIL_REGEX, (match) => {
    const token = `\u0003${emails.length}\u0004`;
    emails.push(match.toLowerCase());
    return token;
  });

  let normalized = normalizePlainTextCase(protectedText);
  normalized = normalized.replace(/\u0003(\d+)\u0004/g, (_, idx) => emails[Number(idx)] || "");
  normalized = normalized.replace(/\u0001(\d+)\u0002/g, (_, idx) => templateTokens[Number(idx)] || "");
  return normalized;
};

const normalizeTextNode = (textNode) => {
  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;
  if (hasSkippableAncestor(textNode)) return;
  const value = textNode.nodeValue || "";
  if (!value.trim()) return;

  const normalized = normalizeDisplayText(value);
  if (normalized !== value) textNode.nodeValue = normalized;
  if (textNode.parentElement) textNode.parentElement.style.textTransform = "none";
};

const normalizeRootText = (rootNode) => {
  if (!rootNode) return;
  if (rootNode.nodeType === Node.TEXT_NODE) {
    normalizeTextNode(rootNode);
    return;
  }
  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    normalizeTextNode(current);
    current = walker.nextNode();
  }
};

const normalizeEmailInputs = (root = document) => {
  const emailInputs = root.querySelectorAll?.('input[type="email"]') || [];
  emailInputs.forEach((input) => {
    input.style.textTransform = "none";
    if (typeof input.value === "string" && input.value) {
      const lower = input.value.toLowerCase();
      if (lower !== input.value) input.value = lower;
    }
    const placeholder = input.getAttribute("placeholder");
    if (placeholder) input.setAttribute("placeholder", normalizeDisplayText(placeholder));
  });
};

const normalizeGenericPlaceholders = (root = document) => {
  const elements = root.querySelectorAll?.("[placeholder]") || [];
  elements.forEach((el) => {
    if (el.tagName === "INPUT" && String(el.getAttribute("type") || "").toLowerCase() === "email") return;
    const placeholder = el.getAttribute("placeholder");
    if (!placeholder || looksLikeUserCodeText(placeholder)) return;
    const normalized = normalizeDisplayText(placeholder);
    if (normalized !== placeholder) el.setAttribute("placeholder", normalized);
  });
};

export const useDomTextCasingNormalizer = () => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    normalizeRootText(document.body);
    normalizeEmailInputs(document);
    normalizeGenericPlaceholders(document);

    let rafId = null;
    const pendingRoots = new Set();

    const flushPending = () => {
      rafId = null;
      pendingRoots.forEach((node) => {
        normalizeRootText(node);
        if (node?.querySelectorAll) {
          normalizeEmailInputs(node);
          normalizeGenericPlaceholders(node);
        }
      });
      pendingRoots.clear();
    };

    const scheduleNormalize = (node) => {
      if (!node) return;
      pendingRoots.add(node);
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(flushPending);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => scheduleNormalize(node));
          return;
        }
        if (mutation.type === "attributes") {
          scheduleNormalize(mutation.target);
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["placeholder"],
    });

    return () => {
      observer.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, []);
};
